import SignupSampleDashboard from '@/components/signup/SignupSampleDashboard';
import {
  Bot,
  Clock,
  Mail,
  MessageSquarePlus,
  Star,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setShowOnlyUnreadConversations } from '@/actions/webchat';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { cn } from '@/lib/utils';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import { getRecentConversations, type RecentConversation } from '@/utils/recentConversations';

interface InboxOverviewProps {
  handleCustomerDialogIsOpen: (open: boolean) => void;
}

const RECENT_HOVER_COLORS = [
  'hsl(210 80% 94% / 0.45)',
  'hsl(260 70% 94% / 0.40)',
  'hsl(170 60% 94% / 0.35)',
];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(name?: string): string {
  if (!name) return '';
  return name.split(' ')[0];
}

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isConversationUnread(
  convo: any,
  userId: string,
  hasOwnIsReadIndicators: boolean,
): boolean {
  if (hasOwnIsReadIndicators && convo._readByUsers) {
    return !convo._readByUsers.map((id: string) => id.toString()).includes(userId);
  }
  return !convo.isRead;
}

function isAssignedToUser(convo: any, userId: string): boolean {
  if (convo._assignees?.length > 0) {
    return convo._assignees.includes(userId);
  }
  return convo._assignee === userId;
}

function isUnassigned(convo: any): boolean {
  return !(convo._assignees?.length > 0) && !convo._assignee && !convo._department;
}

export default function InboxOverview({ handleCustomerDialogIsOpen }: InboxOverviewProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const maskPhone = usePhoneMask();
  const customerConversations: any[] = useSelector(
    (state: any) => state.webchat?.customerConversations ?? [],
  );
  const examplesReady = useSelector((state: any) => state.webchat?.loading === false && !!state.webchat.lastCustomerConversationsSyncedAt && !state.webchat.showOnlyUnreadConversations && !state.webchat.channelFilter && state.webchat.openConversationsSelected !== false);
  const pendingEscalationCount = useSelector(
    (state: any) => state.aiEmployee?.pendingEscalationCount ?? 0,
  );

  const user = auth?.user;
  const hasAiProduct = hasAiEmployeeProduct(auth);

  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: recompute only when the business or enterprise flag changes, not on every auth refresh
  useEffect(() => {
    setRecentConversations(getRecentConversations(auth));
  }, [auth?._id, auth?.user?.isEnterpriseEnabled]);

  const summary = useMemo(() => {
    if (!user?._id) return null;
    const userId = user._id;
    const hasOwnIsReadIndicators = !!user.hasOwnIsReadIndicators;

    const openConvos = customerConversations.filter((c) => c.open);

    let unreadCount = 0;
    let assignedToMeCount = 0;
    let unassignedCount = 0;

    for (const convo of openConvos) {
      if (isConversationUnread(convo, userId, hasOwnIsReadIndicators)) unreadCount++;
      if (isAssignedToUser(convo, userId)) assignedToMeCount++;
      if (isUnassigned(convo)) unassignedCount++;
    }

    return { unreadCount, assignedToMeCount, unassignedCount };
  }, [customerConversations, user?._id, user?.hasOwnIsReadIndicators]);

  const stats = buildStats(summary, pendingEscalationCount, hasAiProduct, navigate, dispatch);
  return (
    <div
      className={cn(
        'relative flex flex-col flex-grow items-center p-6 overflow-y-auto',
        'bg-background',
      )}
    >
      <div
        aria-hidden
        className="inbox-overview-mesh pointer-events-none absolute inset-0 overflow-hidden"
      />
      <div className="relative z-10 w-full max-w-[480px] my-auto space-y-6 animate-in fade-in duration-500">
        {/* Greeting */}
        <div className="text-center space-y-1">
          <h2 className="text-2xl font-semibold text-foreground tracking-tight">
            {getGreeting()}
            {user?.name ? `, ${getFirstName(user.name)}` : ''}
          </h2>
          <p className="text-sm text-muted-foreground">Here&apos;s what&apos;s happening</p>
        </div>

        {/* Stats */}
        {summary && (
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {stats.map((stat) => (
              <Button
                key={stat.label}
                variant="outline"
                size="sm"
                className="rounded-full gap-2"
                onClick={stat.onClick}
              >
                <stat.icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{stat.count}</span>
                <span className="text-muted-foreground">{stat.label}</span>
              </Button>
            ))}
          </div>
        )}

        <Separator />

        <SignupSampleDashboard feature="conversations" realCount={customerConversations.length} ready={examplesReady} />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-onboarding="inbox-start-chat"
            variant="outline"
            className="h-auto py-3 justify-start gap-2.5"
            onClick={() => handleCustomerDialogIsOpen(true)}
          >
            <MessageSquarePlus className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>Start new chat</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-3 justify-start gap-2.5"
            onClick={() => navigate('/customers')}
          >
            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>View contacts</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-3 justify-start gap-2.5"
            onClick={() => navigate('/reviews')}
          >
            <Star className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>See reviews</span>
          </Button>

          {hasAiProduct ? (
            <Button
              variant="outline"
              className="h-auto py-3 justify-start gap-2.5"
              onClick={() => navigate('/ai/overview')}
            >
              <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>AI Employee</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-3 justify-start gap-2.5"
              onClick={() => navigate('/templates')}
            >
              <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>Mannage templates</span>
            </Button>
          )}
        </div>

        {/* Recent conversations */}
        {recentConversations.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Recently viewed conversations
              </p>
              <div className="grid gap-0.5">
                {recentConversations.map((convo, idx) => (
                  <button
                    type="button"
                    key={convo._id}
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-200"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        RECENT_HOVER_COLORS[idx % RECENT_HOVER_COLORS.length])
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() =>
                      navigate(
                        buildCanonicalCustomerConversationPath({
                          _customerConversation: convo._id,
                          _business: convo._business,
                        }),
                      )
                    }
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-foreground">
                          {maskPhone(convo.customerName)}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                          <Clock className="h-3 w-3" />
                          {timeAgo(convo.visitedAt)}
                        </span>
                      </div>
                      {convo.lastMessagePreview && (
                        <p className="truncate text-xs text-muted-foreground mt-0.5">
                          {convo.lastMessagePreview}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function buildStats(
  summary: {
    unreadCount: number;
    assignedToMeCount: number;
    unassignedCount: number;
  } | null,
  escalationCount: number,
  hasAiProduct: boolean,
  navigate: (path: string) => void,
  dispatch: any,
) {
  if (!summary) return [];

  const stats: {
    label: string;
    count: number;
    icon: any;
    onClick: () => void;
  }[] = [
    {
      label: 'unread',
      count: summary.unreadCount,
      icon: Mail,
      onClick: () => {
        dispatch(setShowOnlyUnreadConversations(true));
        navigate('/inbox');
      },
    },
    {
      label: 'assigned to me',
      count: summary.assignedToMeCount,
      icon: UserCheck,
      onClick: () => navigate('/my-messages'),
    },
    {
      label: 'unassigned',
      count: summary.unassignedCount,
      icon: UserX,
      onClick: () => navigate('/unassigned'),
    },
  ];

  if (hasAiProduct && escalationCount > 0) {
    stats.push({
      label: 'escalations',
      count: escalationCount,
      icon: Bot,
      onClick: () => navigate('/ai/escalations'),
    });
  }

  return stats;
}
